import { useContext, useEffect, useMemo, useState } from 'react'

import { Box, GlobalStyles, IconButton, Divider, alpha } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { schema } from '@pkg/zero'
import { Zero } from '@rocicorp/zero'
import { ZeroProvider } from '@rocicorp/zero/react'
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarRightExpand,
} from '@tabler/icons-react'
import ReactGA from 'react-ga4'
import { Toaster } from 'react-hot-toast'
import { useHotkeys } from 'react-hotkeys-hook'
import { v4 as uuid } from 'uuid'

import { useLocation } from '@redwoodjs/router'
import { useRouteName } from '@redwoodjs/router'

import { useAuth } from 'src/auth'
import { AvatarCacheProvider } from 'src/components/AvatarCacheProvider/AvatarCacheProvider'
import { ObjectCacheProvider } from 'src/components/ObjectCacheProvider/ObjectCacheProvider'
import ObjectsProvider from 'src/components/Objects/ObjectsProvider'
import PipelinesProvider from 'src/components/PipelineV3/PipelinesProvider'
import SearchModal from 'src/components/Search/SearchModal/SearchModal'
import Sidebar from 'src/components/Sidebar/Sidebar'
import UserErrorRemediation from 'src/components/UserErrorRemediation/UserErrorRemediation'
import WorkspaceInviteBanner from 'src/components/WorkspaceInviteBanner/WorkspaceInviteBanner'
import {
  NavigationProvider,
  useNavigation,
  useNavigationWidth,
} from 'src/contexts/NavigationContext'
import NavigationContent from 'src/layouts/ModernLayout/NavigationContent'
import WorkspaceDropdown from 'src/layouts/ModernLayout/WorkspaceDropdown/WorkspaceDropdown'
import { DayContext, DayProvider } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import { shadows } from 'src/lib/style'

ReactGA.initialize(process.env.GOOGLE_ANALYTICS_ID, {
  nonce: uuid(),
})

type ModernLayoutProps = {
  children?: React.ReactNode
}

const createZeroInstance = (
  userId: string | undefined,
  getToken: () => Promise<string | null>
) =>
  new Zero({
    userID: userId ?? 'anonymous',
    auth: userId ? () => getToken() : undefined,
    server:
      process.env.NODE_ENV === 'production'
        ? 'https://sync.prod.day.ai'
        : 'https://sync.dev.day.ai',
    schema,
    kvStore: 'idb',
    logLevel: 'debug',
    hiddenTabDisconnectDelay: 60_000,
  })

const globalStyles = {
  body: {
    overflow: 'hidden',
    overscrollBehaviorX: 'none',
    input: {
      backgroundColor: 'transparent',
    },
  },
}

const ModernLayoutInner = ({ children }: ModernLayoutProps) => {
  const { currentUser, getToken } = useAuth()
  const [searchOpen, setSearchOpen] = useState(false)
  const {
    primaryNavCollapsed,
    setPrimaryNavCollapsed,
    primaryDrawerHovered,
    setPrimaryDrawerHovered,
  } = useNavigation()
  const { primaryWidth } = useNavigationWidth()
  const [isTransitioningFromDrawer, setIsTransitioningFromDrawer] =
    useState(false)

  // Bind to command/ctrl + k
  useHotkeys(
    'mod+k',
    (event) => {
      event.preventDefault()
      setSearchOpen(true)
    },
    { enableOnFormTags: true }
  )

  const {
    userCoreContact,
    sidebarObject,
    setSidebarObject,
    selectedWorkspace,
    workspaces,
  } = useContext(DayContext)

  const workspaceInvites = useMemo(
    () => workspaces?.filter((workspace) => workspace.status === 'INVITED'),
    [workspaces]
  )

  const z = useMemo(() => {
    if (!currentUser?.id) {
      return null
    }

    return createZeroInstance(currentUser.id, getToken)
  }, [currentUser?.id])

  useEffect(() => {
    return () => {
      z?.close()
    }
  }, [z])

  const containerSx = useMemo(() => {
    return {
      color: (theme) => theme.palette.text.primary,
      background: (theme) => theme.palette.bottom.main,
      display: 'flex',
      flexDirection: 'row',
      height: '100vh',
      overflow: 'hidden',
      '& .sidebar': {
        color: (theme) => theme.palette.text.primary,
        background: primaryNavCollapsed
          ? 'transparent'
          : (theme) => theme.palette.bottom.main,
        width: primaryNavCollapsed ? `${primaryWidth}px` : '240px',
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        borderRight: primaryNavCollapsed
          ? 'none'
          : (theme) => `1px solid ${theme.palette.divider}`,
        transition: isTransitioningFromDrawer ? 'none' : 'all 250ms ease',
        transform: primaryNavCollapsed ? 'translateX(-192px)' : 'translateX(0)',
        '& .sidebar-divider': {
          mx: '12px',
          mb: '12px',
        },
      },
      '& .main-content': {
        width: '100%',
        height: '100vh',
        pl: `${primaryWidth}px`,
        pr: 0,
        py: 0,
        m: 0,
        pt: 0,
        overflow: 'hidden',
        zIndex: 0,
        background: (theme) => theme.palette.background.paper,
        transition: 'padding-left 250ms ease',
      },
    }
  }, [primaryNavCollapsed, primaryWidth, isTransitioningFromDrawer])

  return (
    currentUser && (
      <ZeroProvider zero={z}>
        <ObjectsProvider workspaceId={selectedWorkspace}>
          <PipelinesProvider>
            <GlobalStyles styles={globalStyles} />
            <Toaster />
            <CssBaseline />
            {selectedWorkspace && (
              <SearchModal
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
                allowCreation={true}
                onSelect={(result) => {
                  setSidebarObject({
                    objectType: result.objectType,
                    objectId: result.objectId,
                    properties: {
                      ...result.properties,
                      workspaceId: selectedWorkspace,
                    },
                    workspaceId: selectedWorkspace,
                  })
                  setSearchOpen(false)
                }}
                workspaceId={selectedWorkspace}
              />
            )}

            <Sidebar
              crmObject={sidebarObject}
              setCrmObject={setSidebarObject}
              onClose={() => {}}
              workspaceId={selectedWorkspace}
            />

            <Box sx={containerSx}>
              <Box
                className="sidebar"
                component="div"
              >
                {primaryNavCollapsed ? null : (
                  <>
                    <Box sx={{ position: 'relative' }}>
                      <WorkspaceDropdown />
                      {/* Collapse button - shows on hover when nav is open */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          opacity: 0,
                          transition: 'opacity 250ms ease',
                          '.sidebar:hover &': {
                            opacity: 1,
                          },
                        }}
                      >
                        <IconButton
                          onClick={() => setPrimaryNavCollapsed(true)}
                          size="small"
                          sx={{
                            width: 28,
                            height: 28,
                            backgroundColor: (theme) =>
                              alpha(theme.palette.background.paper, 0.8),
                            '&:hover': {
                              backgroundColor: (theme) =>
                                theme.palette.background.paper,
                            },
                          }}
                        >
                          <IconLayoutSidebarLeftCollapse size={16} />
                        </IconButton>
                      </Box>
                    </Box>
                    <Divider className="sidebar-divider" />
                    <NavigationContent
                      onSearchClick={() => setSearchOpen(true)}
                      selectedWorkspace={selectedWorkspace}
                      userCoreContact={userCoreContact}
                    />
                  </>
                )}
              </Box>

              {/* Navigation drawer - appears on hover when collapsed */}
              {primaryNavCollapsed && (
                <Box
                  sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: `${primaryWidth}px`,
                    height: '100vh',
                    zIndex: 10,
                    backgroundColor: (theme) =>
                      theme.palette.background.default,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    pt: '6px',
                  }}
                  onMouseEnter={() => setPrimaryDrawerHovered(true)}
                >
                  <IconButton
                    onClick={() => setPrimaryNavCollapsed(false)}
                    sx={{
                      width: 32,
                      height: 32,
                      color: (theme) => theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.background.paper,
                      },
                    }}
                  >
                    <IconLayoutSidebarRightExpand
                      size={18}
                      style={{ transform: 'scaleX(-1)' }}
                    />
                  </IconButton>
                </Box>
              )}

              {/* Navigation drawer content */}
              {primaryNavCollapsed && primaryDrawerHovered && (
                <Box
                  sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '240px',
                    height: '100vh',
                    backgroundColor: (theme) => theme.palette.bottom.main,
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    boxShadow: (theme) => shadows[theme.palette?.mode]?.lg,
                    zIndex: 1200,
                    display: 'flex',
                    flexDirection: 'column',
                    transform: 'translateX(0)',
                    animation: 'slideIn 250ms ease-out',
                    '@keyframes slideIn': {
                      from: {
                        transform: 'translateX(-240px)',
                      },
                      to: {
                        transform: 'translateX(0)',
                      },
                    },
                  }}
                  onMouseLeave={() => setPrimaryDrawerHovered(false)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <WorkspaceDropdown />
                    {/* Expand button - pin drawer open */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          setIsTransitioningFromDrawer(true)
                          setPrimaryNavCollapsed(false)
                          setPrimaryDrawerHovered(false)
                          setTimeout(
                            () => setIsTransitioningFromDrawer(false),
                            50
                          )
                        }}
                        size="small"
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: (theme) =>
                            alpha(theme.palette.background.paper, 0.8),
                          '&:hover': {
                            backgroundColor: (theme) =>
                              theme.palette.background.paper,
                          },
                        }}
                      >
                        <IconLayoutSidebarLeftCollapse
                          size={16}
                          style={{ transform: 'scaleX(-1)' }}
                        />
                      </IconButton>
                    </Box>
                  </Box>
                  <Divider sx={{ mx: '12px', mb: '12px' }} />
                  <NavigationContent
                    onSearchClick={() => setSearchOpen(true)}
                    selectedWorkspace={selectedWorkspace}
                    userCoreContact={userCoreContact}
                  />
                </Box>
              )}

              <Box className="main-content">
                <UserErrorRemediation />
                <WorkspaceInviteBanner workspaceInvites={workspaceInvites} />
                {children}
              </Box>
            </Box>
          </PipelinesProvider>
        </ObjectsProvider>
      </ZeroProvider>
    )
  )
}

const Ga4ContentGroup = {
  today: 'Home',
  prospects: 'Sales',
  opportunities: 'Sales',
  recordings: 'Meetings',
  marketing: 'Marketing',
  customers: 'Customers',
  pages: 'Pages',
  objectDetail: 'Sales',
  people: 'Data',
  pipelines: 'Sales',
  context: 'Data',
  reviewMeeting: 'Meetings',
  pageDetail: 'Pages',
  workspaceMeetingRecording: 'Meetings',
  workspaceLibrary: 'Meetings',
  library: 'Meetings',
  home: 'Home',
  settings: 'Data',
  actions: 'Data',
  organizations: 'Data',
  reportsPipeline: 'Sales',
  customerInbox: 'Customers',
  customersCurrent: 'Customers',
  product: 'Product',
  marketingContent: 'Marketing',
  marketingCampaigns: 'Marketing',
}

const ModernLayout = ({ children }) => {
  const location = useLocation()
  const route = useRouteName()
  const { currentUser } = useAuth()

  // Set user ID when the user changes
  useEffect(() => {
    if (currentUser?.id) {
      // Set user ID for GA4
      ReactGA.set({ userId: currentUser.id })
    } else {
      // Clear user ID if not available
      ReactGA.set({ userId: undefined })
    }
  }, [currentUser?.id])

  // Track pageviews separately
  useEffect(() => {
    const path = location?.pathname

    // Use route name as a more concise page title
    // This avoids long dynamic paths with IDs
    const pageTitle = route || 'Unknown Page'

    const payload = {
      hitType: 'pageview' as const,
      page: path,
      title: pageTitle, // Using the route name as the title for conciseness
      contentGroup1: Ga4ContentGroup[route] || 'Other',
    }

    logger.dev('Debug log', { value: { GA4Pageview: payload } })

    if (path) {
      try {
        ReactGA.send(payload)
      } catch (e) {
        logger.error('GA tracking error', e as Error, {
          location: path,
        })
      }
    }
  }, [location?.pathname, route])

  return (
    currentUser && (
      <DayProvider>
        <NavigationProvider>
          <AvatarCacheProvider>
            <ObjectCacheProvider>
              <ModernLayoutInner>{children}</ModernLayoutInner>
            </ObjectCacheProvider>
          </AvatarCacheProvider>
        </NavigationProvider>
      </DayProvider>
    )
  )
}

export default ModernLayout
